import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import { FaEdit, FaTrash } from 'react-icons/fa';
import AddExpenseCategory from './AddExpenseCategory';
import { apiGet } from '../../api/apiClient';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import RequireMenuAccess from '../common/RequireMenuAccess';
import '../common/rtCommon.css';
import '../payments/PaymentTypesList.css';
import RTPageLayout from '../common/RTPageLayout';

const ExpenseCategoriesList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/expenses/categories?status=all');
      const data = response?.data || response || [];
      setCategories(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      console.error('Error fetching expense categories:', err);
      setError('Failed to load expense categories');
      toastError('Failed to load expense categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditCategory(null);
    setShowAddEdit(true);
  };

  const handleEdit = (category) => {
    setEditCategory(category);
    setShowAddEdit(true);
  };

  const handleDelete = async (categoryId, categoryName) => {
    const confirmed = await toastConfirm(
      `Are you sure you want to delete the expense category "${categoryName}"?`,
    );
    if (!confirmed) return;

    try {
      setDeleting(true);
      const response = await apiClient.post(
        `/expenses/categories/${categoryId}/delete`,
      );
      toastSuccess(
        response.data?.message || 'Expense category deleted successfully',
      );
      fetchCategories();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to delete expense category';
      toastError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      sortable: true,
    },
    {
      Header: 'Staff Expense',
      accessor: 'is_staff_expense',
      Cell: ({ value }) => (value ? 'Yes' : 'No'),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => (value === 'ACTIVE' ? 'Active' : 'Inactive'),
    },
    {
      Header: 'Actions',
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }) => {
        const category = row.original || row.values || row;
        return (
          <div style={{ display: 'flex', gap: '10px' }}>
            <FaEdit
              style={{ cursor: 'pointer', color: '#153C7F', fontSize: '16px' }}
              onClick={() => handleEdit(category)}
              title="Edit"
            />
            <FaTrash
              style={{ cursor: 'pointer', color: '#d32f2f', fontSize: '16px' }}
              onClick={() => handleDelete(category.id, category.name)}
              title="Delete"
            />
          </div>
        );
      },
    },
  ];

  if (showAddEdit) {
    return (
      <RequireMenuAccess>
        <RTPageLayout activePath={location.pathname}>
          <AddExpenseCategory
            category={editCategory}
            onClose={() => {
              setShowAddEdit(false);
              fetchCategories();
            }}
          />
        </RTPageLayout>
      </RequireMenuAccess>
    );
  }

  return (
    <RequireMenuAccess>
      <RTPageLayout
        activePath={location.pathname}
        title="Manage Expense Categories"
        actions={
          <>
            <RTButton variant="primary" onClick={handleCreate}>
              Add Category
            </RTButton>
            <RTButton variant="secondary" onClick={() => navigate('/settings')}>
              ← Back
            </RTButton>
          </>
        }
        mainSection={true}
      >
        {loading ? (
          <RTSpinner />
        ) : (
          <>
            {error && <div className="error-message">{error}</div>}
            <div className="table-container">
              <Table
                data={categories}
                columns={columns}
                exportable={false}
                showSearch={true}
                showTotalCount={true}
                className="payment-types-table"
              />
            </div>
          </>
        )}
      </RTPageLayout>
    </RequireMenuAccess>
  );
};

export default ExpenseCategoriesList;
